import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography } from '@mui/material';
import DialogComponent from '../../component/dialog/dialog.component';
import i18n from '../../i18n';
import { Dish, Order, RiskOrder, SourceType } from '../../models';
import apiManage from '../../request';
import {
    applyHistoryOrderDetailAmountFallback,
    cloneOrderDetailTableRowFallback,
    mergeOrderDetailSummaryWithTableRow,
    parseDataAnalysisOrderDetailWithHead,
    type DataAnalysisOrderDetailWithHeadResponse,
} from '../../request/api/data-analysis';
import type { HttpSuccessOrErrorType } from '../../request/http';
import GlobalNotification from '../../component/common/snackbar';
import { accountState } from '../my-account/account-state';
import { formatEventTypeText, formatRiskTypeText } from '../../constants/tool-order';
import {
    OrderDetailDishListBlock,
    OrderDetailOrderSummaryBlock,
    OrderDetailPaymentQueueBlock,
} from '../data-analysis/order-data/order-detail-dialog-blocks';
import { ReactComponent as RiskWarningTriangleIcon } from '../../static/images/svg/risk-order-row-warning.svg';
import { RiskDetailDialogFooter, type RiskDetailDialogActionStatus } from './risk-detail-dialog-footer';
import { mapRiskOrderToOrderDetailFallback } from './risk-associated-order-detail';
import { getRiskOrderIdsForScope, getRiskTagsForOrder, RiskOrderGroup, RiskOrderMode } from './risk-order-mode';

/** 弹窗类型：即时订单详情 / 历史订单详情 */
export enum RiskOrderDetailDialogType {
    ORDER = 'ORDER',
    HISTORY_ORDER = 'HISTORY_ORDER',
}

interface RiskOrderDetailDialogProps {
    open?: boolean;
    onClose?: () => void;
    orderSummary?: RiskOrder | null;
    stringId?: string;
    onSaved?: () => void;
    type: RiskOrderDetailDialogType;
    /** 即时列表当前展示模式，用于决定处理范围。 */
    mode?: RiskOrderMode;
}

type WarningSideProps = {
    label: string;
    value?: React.ReactNode;
};

type WarningSideAlign = 'start' | 'end';

/**
 * 根据警示条位置分配宽度，使右侧风险标签优先使用剩余空间。
 * @param align - 警示条左侧或右侧
 * @returns Flex 伸缩规则
 */
export function getRiskDialogWarningSideFlex(align: WarningSideAlign): string {
    return align === 'start' ? '0 1 auto' : '1 1 auto';
}

/**
 * 风控详情弹窗警示条单侧：三角 + 标签 + 数值（设计稿错误色）；对齐方式与 Figma 左右分布一致
 * @param align - 左块顶左、右块顶右
 */
const RiskDialogWarningSide: React.FC<WarningSideProps & { align: WarningSideAlign }> = ({ label, value, align }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            flex: getRiskDialogWarningSideFlex(align),
            justifyContent: align === 'start' ? 'flex-start' : 'flex-end',
        }}
    >
        <Box
            sx={{
                width: 24,
                height: 24,
                flexShrink: 0,
                color: 'var(--func-error)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            aria-hidden
        >
            <RiskWarningTriangleIcon width={24} height={24} />
        </Box>
        <Typography variant="body2" sx={{ color: 'var(--func-error)' }}>
            {label}
        </Typography>
        <Typography variant="subtitle2" sx={{ color: 'var(--func-error)' }}>
            {value ?? '-'}
        </Typography>
    </Box>
);

/**
 * 设计稿警示区：左侧固定操作人员，右侧展示订单的全部风险标签。
 * @param order - 风控订单摘要
 */
export function getRiskDialogWarningSides(order: RiskOrderGroup): { left: WarningSideProps; right: WarningSideProps } {
    return {
        left: {
            label: `${i18n.t('business_analysis_page_operator_person')}：`,
            value: order.operator_name ?? '-',
        },
        right: {
            label: `${i18n.t('intelligent_risk_control_page_filter_risk_tag')}：`,
            value: getRiskDialogRiskTagText(order),
        },
    };
}

/**
 * 获取订单详情弹窗的通用标题，不在标题中拼接风险类型。
 * @returns 已翻译的订单详情标题
 */
export function getRiskDialogTitleText(): string {
    return i18n.t('intelligent_risk_control_page_dialog_title_order_detail');
}

/**
 * 获取订单聚合后的全部去重风险标签。
 * @param order - 订单模式聚合的风控订单
 * @returns 规则风险与事件风险标签，使用顿号连接
 */
export function getRiskDialogRiskTagText(order: RiskOrderGroup): string {
    return getRiskTagsForOrder(order)
        .map((tag) => i18n.t(tag.type === 'risk' ? formatRiskTypeText(tag.value) : formatEventTypeText(tag.value)))
        .join('、') || '-';
}

/**
 * 获取订单聚合处理请求的风险来源类型。
 * @param order - 订单模式聚合的风控订单
 * @returns 仅事件风险返回 EVENT，否则沿用订单来源或 ORDER
 */
export function getRiskOrderTargetType(order: RiskOrderGroup): SourceType {
    const hasRuleRisks = Boolean(
        order.riskOrders?.length ||
        order.risk_types?.length ||
        order.risk_type
    );
    /** 事件风险既可能由嵌套记录表示，也可能仅由聚合后的事件类型表示。 */
    const hasEventRisks = Boolean(order.riskEvents?.length || order.event_types?.length);
    if (!hasRuleRisks && hasEventRisks) {
        return SourceType.EVENT;
    }
    return order.source_type || SourceType.ORDER;
}

/**
 * 提取订单模式事件风险关联的订单 ID。
 * @param order - 订单模式聚合的风控订单
 * @returns 有效且去重后的事件订单 ID
 */
export function getRiskEventOrderIds(order: RiskOrderGroup): number[] {
    const eventOrderIds = (order.riskEvents ?? [])
        .map((event) => event.event_order_id)
        .filter((id): id is number | string => {
            if (typeof id === 'number') {
                return Number.isFinite(id);
            }
            return typeof id === 'string' && id.trim() !== '' && Number.isFinite(Number(id));
        })
        .map(Number);

    return Array.from(new Set(eventOrderIds));
}

/**
 * 风险订单列表-综合操作弹窗（布局与 Figma 订单详情 2827:110660 一致：摘要 + 警示条 + 菜品表 + 底部操作）
 */
const RiskOrderDetailDialog: React.FC<RiskOrderDetailDialogProps> = observer((props) => {
    const { open, onClose, orderSummary, stringId, onSaved } = props;

    /**
     * 关闭弹窗
     */
    const handleClose = () => {
        onClose?.();
    };

    /** 菜品详情列表，用于弹窗内表格展示（来源：即时订单接口 details，历史订单接口为 Order[0].menu） */
    const [orderDetailDish, setOrderDetailDish] = useState<Dish[]>([]);
    /** 即时订单摘要：来自 order_detail_with_head 的 order_head，非风控列表行 */
    const [orderDetailSummary, setOrderDetailSummary] = useState<Order | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    /** 底部操作提交中：1 忽略 / 2 确认已处理；与 LoadingOverlayButton.loading 同步 */
    const [actionSubmitting, setActionSubmitting] = useState<RiskDetailDialogActionStatus>(null);
    /** 当前风控状态原始值，按列表归一逻辑兼容多个接口字段 */
    const rawRiskStatus = orderSummary?.orderStatus ?? orderSummary?.risk_status ?? orderSummary?.status;
    /** 仅明确的 0 状态允许修改，缺失或非法状态默认只读 */
    const canModifyRiskStatus =
        rawRiskStatus !== undefined && rawRiskStatus !== null && Number(rawRiskStatus) === 0;

    /**
     * 提交处理结果（底部按钮对应状态：1 已忽略 / 2 已解决，与设计稿一致）
     * @param nextStatus - 目标风控状态
     */
    const performSave = async (nextStatus: number) => {
        if (!stringId || (!orderSummary?.id && !orderSummary?.order_head_id)) return;
        if (!canModifyRiskStatus) return;
        if (actionSubmitting !== null) return;
        const s = nextStatus;
        if (s === 0) {
            GlobalNotification.error(i18n.t('common_text_cannot_update_status_to_pending_confirmation'));
            return;
        }
        const submittingStatus = s === 1 ? 1 : 2;
        setActionSubmitting(submittingStatus);
        try {
            let res;
            if (props.type === RiskOrderDetailDialogType.HISTORY_ORDER) {
                const historyRiskId = orderSummary.id ?? getRiskOrderIdsForScope(orderSummary as RiskOrderGroup, 'ORDER')[0];
                if (historyRiskId === undefined) {
                    GlobalNotification.error(i18n.t('general_text_operate_failure'));
                    setActionSubmitting(null);
                    return;
                }
                res = await apiManage.patch_cloud_restaurant_risk(stringId, {
                    id: historyRiskId,
                    status: s === 0 ? undefined : s,
                    target_type: getRiskOrderTargetType(orderSummary as RiskOrderGroup),
                    operator_name: accountState.userInfo?.nickname ?? '',
                    scope: 'ORDER',
                });
            } else {
                const groupedOrder = orderSummary as RiskOrderGroup;
                const ids = getRiskOrderIdsForScope(groupedOrder, 'ORDER');
                /** 订单聚合处理整单；风险点模式仅处理当前风险记录。 */
                const scope = props.mode === 'risk' ? 'RISK' : 'ORDER';
                const targetType =
                    props.mode === 'risk' ? SourceType.ORDER : getRiskOrderTargetType(groupedOrder);
                const eventOrderIds =
                    scope === 'ORDER' ? getRiskEventOrderIds(groupedOrder) : [];
                res = await apiManage.patch_restaurant_risk_handle(stringId, {
                    ids: ids.length > 0 ? ids : [orderSummary.id].filter((id): id is number => typeof id === 'number'),
                    status: s === 0 ? undefined : s,
                    target_type: targetType,
                    operator_name: accountState.userInfo?.nickname ?? '',
                    scope,
                    ...(eventOrderIds.length > 0 ? { event_order_ids: eventOrderIds } : {}),
                });
            }

            if (!res?.error) {
                GlobalNotification.success(i18n.t('general_text_modify_success'));
                onSaved?.();
                onClose?.();
            } else {
                GlobalNotification.error(i18n.t('general_text_modify_failed'));
            }
        } finally {
            setActionSubmitting(null);
        }
    };

    useEffect(() => {
        const orderHeadId = orderSummary?.order_head_id;
        const serverId = orderSummary?.server_id ?? '';
        if (!open || !stringId || !orderHeadId) return;
        if (props.type === RiskOrderDetailDialogType.HISTORY_ORDER && !serverId) return;

        let cancelled = false;

        const tableFallback = orderSummary
            ? cloneOrderDetailTableRowFallback(mapRiskOrderToOrderDetailFallback(orderSummary))
            : null;

        const fetchDetail = async () => {
            setDetailLoading(true);
            setOrderDetailSummary(tableFallback);
            setOrderDetailDish([]);
            try {
                const res =
                    props.type === RiskOrderDetailDialogType.ORDER
                        ? await apiManage.get_data_analysis_order_details({
                              string_id: stringId,
                              order_head_id: `${orderHeadId}`,
                              sort: 'ASC',
                          })
                        : await apiManage.get_data_analysis_res_history_order_details({
                              string_id: stringId,
                              id: serverId,
                          });

                if (cancelled) return;

                const { order, details } = parseDataAnalysisOrderDetailWithHead(
                    res as HttpSuccessOrErrorType<DataAnalysisOrderDetailWithHeadResponse>
                );
                if (!cancelled) {
                    const hasError =
                        res != null &&
                        typeof res === 'object' &&
                        'error' in res &&
                        Boolean((res as { error?: boolean }).error);
                    if (!hasError && tableFallback) {
                        const apiOrder =
                            props.type === RiskOrderDetailDialogType.HISTORY_ORDER
                                ? applyHistoryOrderDetailAmountFallback(order)
                                : order;
                        setOrderDetailSummary(
                            mergeOrderDetailSummaryWithTableRow(tableFallback, apiOrder)
                        );
                        setOrderDetailDish(details ?? []);
                    } else if (!hasError) {
                        setOrderDetailDish(details ?? []);
                    } else if (tableFallback) {
                        setOrderDetailSummary(tableFallback);
                    }
                }
            } catch {
                if (!cancelled && tableFallback) {
                    setOrderDetailSummary(tableFallback);
                    setOrderDetailDish([]);
                }
            } finally {
                if (!cancelled) setDetailLoading(false);
            }
        };

        fetchDetail();
        return () => {
            cancelled = true;
        };
    }, [open, stringId, orderSummary?.order_head_id, orderSummary?.server_id, props.type]);

    const orderHead = orderDetailSummary;
    const warningSides = orderSummary ? getRiskDialogWarningSides(orderSummary as RiskOrderGroup) : null;
    /** 订单详情弹窗固定使用通用标题。 */
    const titleText = orderSummary ? getRiskDialogTitleText() : '';

    const titleContent = orderSummary ? (
        <Typography variant="h3" component="span" className="header-title">
            {titleText}
        </Typography>
    ) : undefined;

    /**
     * 底部双按钮：与设计稿 2830:111080 / 2830:111078 一致（忽略此次风控 → 状态 1，确认已处理 → 状态 2）
     */
    return (
        <DialogComponent
            fullWidth={true}
            heightSize="large"
            defaultHeaderProps={{
                titleContent,
                onClose: handleClose,
                titleAlign: 'center',
            }}
            maxWidth='lg'
            open={!!open}
            onClose={handleClose}
            footer={
                canModifyRiskStatus ? (
                    <RiskDetailDialogFooter
                        actionSubmitting={actionSubmitting}
                        onIgnore={() => void performSave(1)}
                        onConfirm={() => void performSave(2)}
                    />
                ) : undefined
            }
        >
            <Box
                sx={{
                    px: 2,
                    pb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <OrderDetailOrderSummaryBlock order={orderHead} layout="risk" />
                <OrderDetailPaymentQueueBlock order={orderHead} />
                {warningSides ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            bgcolor: 'common.white',
                            borderRadius: '12px',
                            border: '1px solid var(--func-error)',
                            p: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <RiskDialogWarningSide {...warningSides.left} align="start" />
                        <RiskDialogWarningSide {...warningSides.right} align="end" />
                    </Box>
                ) : null}
                <OrderDetailDishListBlock list={orderDetailDish} isTableLoading={detailLoading} />
            </Box>
        </DialogComponent>
    );
});

export default RiskOrderDetailDialog;
